import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, ApiError } from "../lib/api";
import type {
  DocumentDTO,
  PartnerDetail as PartnerDetailDTO,
  PayoutMethodSnapshot,
  ReferralDTO,
  ReferralStage,
} from "../../shared/types";
import { money, num } from "../lib/format";
import { useNative } from "../../shared/phone/media";
import { PartnerScreen } from "./partners/PartnerScreen";
import { dayLabel, initials, linkParts, STAGE_LABEL } from "./partners/parts";
import { methodSummary } from "./partners/payout";
import { usePartnerDetail } from "./partners/usePartnerDetail";
import "../styles/partner-detail.css";

// ── helpers ──────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── referral stage presentation ────────────────────────────────────────────

const STAGE_PILL: Record<ReferralStage, string> = {
  click: "pending",
  signup: "pending",
  trial: "aff",
  customer: "paidout",
  onhold: "due",
  churned: "pending",
};

// ── the owed-position wording under the header KPI ───────────────────────────

function owedSub(detail: PartnerDetailDTO): string {
  const { payout } = detail;
  if (payout.state === "ready") return "ready to pay";
  if (payout.state === "blocked") return "on hold";
  if (payout.state === "below_min") return "below the minimum";
  return payout.lastPaidAt ? "paid up" : "nothing due";
}

// ── icons ────────────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg className="bk-ic" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

// ── a small copy control (link / email) ──────────────────────────────────────

function CopyChip({ text, className, ariaLabel }: { text: string; className: string; ariaLabel: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1100);
    } catch {
      // Clipboard denied (permissions / insecure context): the value stays on screen to copy by hand.
    }
  };
  return (
    <button type="button" className={`${className} copychip${copied ? " copied" : ""}`} onClick={copy} aria-label={ariaLabel}>
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ── mark-paid modal (RECORDS a payment; never moves money) ──────────────────

interface MarkPaidRecord {
  amountCents: number;
  reference: string | null;
  note: string | null;
  datePaid: string | null;
}

function MarkPaidModal({
  name,
  owedCents,
  method,
  onClose,
  onRecorded,
}: {
  name: string;
  owedCents: number;
  method: PayoutMethodSnapshot | null;
  onClose: () => void;
  onRecorded: (record: MarkPaidRecord) => Promise<void> | void;
}) {
  const [amount, setAmount] = useState((owedCents / 100).toFixed(2));
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (busy) return;
    const cents = Math.round(Number.parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setErr("Enter an amount greater than zero.");
      return;
    }
    if (cents > owedCents) {
      setErr(`That is more than the ${money(owedCents)} owed to ${name}.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onRecorded({
        amountCents: cents,
        reference: reference.trim() || null,
        note: note.trim() || null,
        datePaid: date || null,
      });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div
      className="pd-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pd-modal" role="dialog" aria-modal="true" aria-label={`Mark ${name} paid`}>
        <div className="mhead">
          <h3>Mark paid: {name}</h3>
          <button type="button" className="mx" onClick={onClose} ref={closeRef} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="mbody">
          <div className="mp-line">
            <span>Send to</span>
            <b>{methodSummary(method)}</b>
          </div>
          <div className="field mp-amount">
            <label htmlFor="pd-mp-amount">Amount</label>
            <span className="cur">$</span>
            <input
              id="pd-mp-amount"
              className="inp"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pd-mp-date">Date paid</label>
            <input id="pd-mp-date" className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pd-mp-ref">Reference / transaction ID</label>
            <input
              id="pd-mp-ref"
              className="inp"
              value={reference}
              placeholder="Paste the transfer or PayPal ID"
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pd-mp-note">
              Note to partner <span className="opt">optional</span>
            </label>
            <input
              id="pd-mp-note"
              className="inp"
              value={note}
              placeholder="Shows on their payout receipt"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {err && <div className="mp-err">{err}</div>}
          <div className="records-note">
            This records the payment. You pay {name} directly with the details above, Flashadz never moves the money.
          </div>
        </div>
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={submit} disabled={busy}>
            {busy ? "Recording…" : "Mark as paid"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── document rows ────────────────────────────────────────────────────────────

/** One document row: label, real sub-line + status pill, and a real View for a signed agreement. */
function DocRow({
  partnerId,
  title,
  sub,
  ok,
  okPill,
  okText,
  agreementDoc,
}: {
  partnerId: string;
  title: string;
  sub: string;
  ok: boolean;
  okPill: string;
  okText: string;
  agreementDoc?: DocumentDTO | null;
}) {
  const viewable = agreementDoc?.signedAt ? agreementDoc : null;
  return (
    <div className="pd-doc">
      <div className="dl">
        <div className="dp">{title}</div>
        <div className="ds">{sub}</div>
      </div>
      {ok ? <span className={`pill ${okPill}`}>{okText}</span> : <span className="pill due">Missing</span>}
      {viewable && (
        <a
          className="btn ghost sm"
          href={api.agreements.partnerDocumentPdfUrl(partnerId, viewable.id)}
          target="_blank"
          rel="noreferrer"
        >
          View
        </a>
      )}
    </div>
  );
}

// ── the partner detail view ──────────────────────────────────────────────────

/** Owner partner detail (`/partners/:id`): the native screen on a phone or an iPad, else the page. */
export function PartnerDetail() {
  const { id = "" } = useParams();
  const native = useNative();
  return native ? <PartnerScreen id={id} /> : <PartnerDetailPage id={id} />;
}

/** The computer's partner page: KPIs, documents, referrals, mark paid. */
function PartnerDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const { detail, loading, notFound, err, refetch } = usePartnerDetail(id);
  const [markOpen, setMarkOpen] = useState(false);

  const [toast, setToast] = useState<{ msg: string; color: string; show: boolean }>({
    msg: "",
    color: "var(--violet)",
    show: false,
  });
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, color = "var(--violet)") => {
    setToast({ msg, color, show: true });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2800);
  }, []);
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);

  const recordPayment = useCallback(
    async (record: MarkPaidRecord) => {
      if (!detail) return;
      const name = `${detail.partner.firstName} ${detail.partner.lastName}`.trim();
      await api.partners.markPaid(detail.partner.id, {
        amountCents: record.amountCents,
        reference: record.reference,
        note: record.note,
        datePaid: record.datePaid,
        methodSnapshot: detail.payoutMethod,
      });
      setMarkOpen(false);
      showToast(`Recorded ${money(record.amountCents)} to ${name}`, "var(--win)");
      // Refetch so the owed position (and the ledger) recomputes from the server.
      await refetch().catch(() => {});
    },
    [detail, refetch, showToast],
  );

  const backTo = detail ? `/products/${encodeURIComponent(detail.program.productId)}/partners` : null;
  const goBack = () => (backTo ? navigate(backTo) : navigate(-1));

  return (
    <section className="partner-detail view-wrap">
      <button type="button" className="back" onClick={goBack}>
        <BackIcon />
        Partners
      </button>

      {loading && <div className="load-note">Loading the partner…</div>}

      {!loading && notFound && (
        <div className="missing">
          <h3>Partner not found</h3>
          <p>This partner does not exist, or they belong to another account.</p>
        </div>
      )}

      {!loading && err && !notFound && <div className="load-note">Couldn't load the partner: {err}</div>}

      {!loading && !err && !notFound && detail && (
        <PartnerDetailBody
          detail={detail}
          onMarkPaid={() => setMarkOpen(true)}
          onManageDocs={() => showToast("Program settings arrive with the next update")}
        />
      )}

      {markOpen && detail && (
        <MarkPaidModal
          name={`${detail.partner.firstName} ${detail.partner.lastName}`.trim()}
          owedCents={detail.payout.readyCents}
          method={detail.payoutMethod}
          onClose={() => setMarkOpen(false)}
          onRecorded={recordPayment}
        />
      )}

      <div className={`pt-toast ${toast.show ? "show" : ""}`}>
        <span className="tdot" style={{ background: toast.color }} />
        <span className="msg">{toast.msg}</span>
      </div>
    </section>
  );
}

// ── the loaded body (kept separate so the states above stay readable) ────────

function PartnerDetailBody({
  detail,
  onMarkPaid,
  onManageDocs,
}: {
  detail: PartnerDetailDTO;
  onMarkPaid: () => void;
  onManageDocs: () => void;
}) {
  const { partner, program, kpis, payout, docStatus, documents, payoutMethod, referrals } = detail;
  const name = `${partner.firstName} ${partner.lastName}`.trim();
  const link = linkParts(program.linkBase, partner.slug);

  const statusPill =
    partner.status === "active"
      ? { cls: "live", text: "Active" }
      : partner.status === "pending"
        ? { cls: "pending", text: "Pending" }
        : partner.status === "paused"
          ? { cls: "due", text: "Paused" }
          : { cls: "pending", text: "Rejected" };

  const agreementDoc = documents.find((d) => d.kind === "agreement") ?? null;
  const agreementSub = docStatus.agreement
    ? `signed${dayLabel(agreementDoc?.signedAt ?? null) ? ` ${dayLabel(agreementDoc?.signedAt ?? null)}` : ""}${
        agreementDoc?.docVersion ? ` · v${agreementDoc.docVersion}` : ""
      } · mandatory`
    : "Not signed yet · mandatory";
  // U0: the prototype's tax sub-line reads "W-9 · US person", but S1's DocumentDTO
  // omits data_json, so the client only has the status ("on file"). We render the
  // real status, not a fabricated form type. Confirm whether S1 should expose the
  // tax form kind (W-9 / W-8BEN + US-person) to the detail. See GRAPH-v2 section 9.
  const taxSub = docStatus.tax ? "On file · mandatory" : "Not provided yet · mandatory";
  const payoutSub = docStatus.payoutMethod ? methodSummary(payoutMethod) : "Not added yet · mandatory";

  const missing: string[] = [];
  if (!docStatus.agreement) missing.push("the signed agreement");
  if (!docStatus.tax) missing.push("the tax form");
  if (!docStatus.payoutMethod) missing.push("a payout method");
  const allOnFile = missing.length === 0;
  const missingText =
    missing.length === 1
      ? missing[0]
      : missing.length === 2
        ? `${missing[0]} and ${missing[1]}`
        : `${missing.slice(0, -1).join(", ")}, and ${missing[missing.length - 1]}`;

  const canPay = payout.state === "ready" && payout.readyCents > 0;

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Partner</div>
          <h1>{name}</h1>
          <div className="setrow id-row">
            <span className={`pill ${statusPill.cls}`}>{statusPill.text}</span>
            {partner.selfReferralFlag && <span className="flag">self-referral</span>}
            <span className="reflink" title={link.full}>
              {link.shown}
            </span>
            <CopyChip text={link.full} className="mini" ariaLabel="Copy referral link" />
            <span className="email">{partner.email}</span>
          </div>
        </div>
        <div className="owed-wrap">
          <div className="owed">
            <div className="k">Owed now</div>
            <div className="v">{money(payout.owedCents)}</div>
            <div className={`d${canPay ? " ready" : ""}`}>{owedSub(detail)}</div>
          </div>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="k">Clicks</div>
          <div className="v">{num(kpis.clicks)}</div>
        </div>
        <div className="kpi">
          <div className="k">Signups</div>
          <div className="v">{num(kpis.regs)}</div>
        </div>
        <div className="kpi">
          <div className="k">Customers</div>
          <div className="v">{num(kpis.sales)}</div>
        </div>
        <div className="kpi">
          <div className="k">Earned</div>
          <div className="v">{money(kpis.earnedCents)}</div>
        </div>
      </div>

      <div className="doc-sechead">
        <span className="dsh-t">Documents</span>
        <span className="dsh-line" />
        <button type="button" className="btn ghost sm" onClick={onManageDocs}>
          Manage required documents
        </button>
      </div>
      <div className="setrow doc-status">
        {allOnFile ? (
          <>
            <span className="pill live">All required on file</span>
            <span className="setnote">Payout unlocked. Required documents are set for the whole program in settings.</span>
          </>
        ) : (
          <>
            <span className="pill due">Payout blocked</span>
            <span className="setnote">Waiting on the partner for {missingText}. Payout unlocks once every required document is on file.</span>
          </>
        )}
      </div>
      <div className="panel docs">
        <DocRow
          partnerId={partner.id}
          title="Partner agreement"
          sub={agreementSub}
          ok={docStatus.agreement}
          okPill="paidout"
          okText="Signed"
          agreementDoc={agreementDoc}
        />
        <DocRow partnerId={partner.id} title="Tax form" sub={taxSub} ok={docStatus.tax} okPill="paidout" okText="On file" />
        <DocRow partnerId={partner.id} title="Payout method" sub={payoutSub} ok={docStatus.payoutMethod} okPill="pending" okText="Saved" />
      </div>

      <div className="slabel">Referrals</div>
      <div className="panel">
        <div className="panel-head">
          <h3>People {partner.firstName} brought in</h3>
          <span className="pill pending">last-click</span>
        </div>
        {referrals.length === 0 ? (
          <div className="tab-empty">No referrals yet. They show up here as people click this partner's link and sign up.</div>
        ) : (
          <div className="tscroll">
            <table className="ptable">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Joined</th>
                  <th>Stage</th>
                  <th className="num">Earned</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <ReferralRow key={r.id} referral={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="setrow pay-row">
        {canPay ? (
          <>
            <button type="button" className="btn" onClick={onMarkPaid}>
              Mark {money(payout.readyCents)} paid
            </button>
            <span className="setnote">You pay them directly. This records it as paid here and on their receipt.</span>
          </>
        ) : payout.owedCents > 0 ? (
          <>
            <button type="button" className="btn" disabled>
              Mark {money(payout.owedCents)} paid
            </button>
            <span className="setnote">{payout.reason ?? "This money cannot be sent yet."}</span>
          </>
        ) : (
          <span className="setnote">
            Nothing to pay right now.
            {payout.lastPaidAt ? ` Last recorded payment ${dayLabel(payout.lastPaidAt)}.` : " New rewards land here once they clear the hold."}
          </span>
        )}
      </div>
    </>
  );
}

/** One referral row: person, joined date, stage pill, earned (or pending / reversed). */
function ReferralRow({ referral }: { referral: ReferralDTO }) {
  const label = referral.personLabel ?? "Someone";
  const earnedCls = referral.reversed
    ? "num dim"
    : referral.earnedCents > 0
      ? `num earn${referral.cleared ? " won" : ""}`
      : "num dim";
  const earnedText = referral.reversed
    ? "reversed"
    : referral.earnedCents > 0
      ? money(referral.earnedCents)
      : "pending";
  return (
    <tr>
      <td>
        <div className="pa">
          <span className="av">{initials(label)}</span>
          <div>
            <div className="pn">{label}</div>
          </div>
        </div>
      </td>
      <td className="num">{dayLabel(referral.joinedAt) || "recently"}</td>
      <td>
        <span className={`pill ${STAGE_PILL[referral.stage]}`}>{STAGE_LABEL[referral.stage]}</span>
      </td>
      <td className={earnedCls}>{earnedText}</td>
    </tr>
  );
}
